import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useImperativeHandle,
  useCallback
} from 'react'
import { Modal, ModalHeader, Spinner } from 'reactstrap'
import {
  IconX,
  IconSearch,
  IconDatabase,
  IconChevronRight,
  IconLock,
  IconExternalLink
} from '@tabler/icons-react'
//@ts-ignore
import { Badge, Button, InputGroup } from 'dsm-react-lib'
import { useDataboxService } from '../services/databoxService'
import { getTierColor, getTierAbbr } from './AddDataProductsModal'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { DATA_PRODUCT_DOMAINS } from '../../Data-marketplace/data'
import { useAppsService } from '../../../../_services/AppsService'
import { launchApp } from '../../../../_redux/actions/workspaceActions'
import { usePost } from '../../../../_hooks/usePost'
import _endpoints from '../../../../_endpoints'
import { toast } from 'react-toastify'
import { staticAssetsPath } from '../../../../configs/constants'
import { useSasToken } from '../../../../_hooks/useSasToken'

interface DataMarketplaceModalProps {
  isOpen: boolean
  toggle: () => void
  onDataProductsAdded?: (products: DataProduct[]) => void
  existingDataProducts?: any[]
  // Ref exposing this modal's imperative handle to the parent
  ref?: React.Ref<DataMarketplaceModalHandle>
}

interface DataProduct {
  id: string
  name: string
  description: string
  tier: 'Business Optimised' | 'Curated' | 'Standard'
  accessStatus?: string
  dataCategory?: string
  dataProviders?: string
  exploreUrl?: string
  owners?: { name: string; department?: string }[]
}

export interface DataMarketplaceModalHandle {
  clearSelection: () => void
}

const DataMarketplaceModal = ({
  isOpen,
  toggle,
  onDataProductsAdded,
  existingDataProducts = [],
  ref
}: DataMarketplaceModalProps) => {
  const { isLoadingDataProducts, fetchAvailableDataProducts } = useDataboxService()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getApplication } = useAppsService()
  const { appendToken } = useSasToken()
  const imgPath = `${staticAssetsPath}/images/dataMarketplace`

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDomain, setSelectedDomain] = useState<{ label: string; value: string } | null>(
    null
  )
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [dataProducts, setDataProducts] = useState<DataProduct[]>([])
  const [hoveredProduct, setHoveredProduct] = useState<DataProduct | null>(null)
  const [accessCache, setAccessCache] = useState<
    Record<string, { hasAccess: boolean; isPending: boolean; isLoading: boolean }>
  >({})
  const pillsRef = useRef<HTMLDivElement>(null)
  const [, submitAccessRequest] = usePost()

  const handleRequestAccess = useCallback(
    async (product: DataProduct) => {
      const id = product.id
      setAccessCache((prev) => ({
        ...prev,
        [id]: { hasAccess: false, isPending: false, isLoading: true }
      }))

      const response = await submitAccessRequest({
        url: _endpoints.dataMarketplace.dataProductArcRequest,
        data: {
          uniqueName: id,
          arcRoles: (product as any)?.arcRoles?.map((role: any) => role.role) || [],
          title: `ARC role request for data product ${product.name}`
        }
      })

      if (response?.status >= 400 || response?.code >= 400) {
        toast.error('An error occurred when submitting your request.')
        setAccessCache((prev) => ({
          ...prev,
          [id]: { hasAccess: false, isPending: false, isLoading: false }
        }))
      } else {
        toast.success(`Request raised successfully for ${product.name}!`)
        setAccessCache((prev) => ({
          ...prev,
          [id]: { hasAccess: false, isPending: true, isLoading: false }
        }))
        setDataProducts((prev) =>
          prev.map((p) => (p.id === id ? { ...p, accessStatus: 'PENDING_ACCESS' } : p))
        )
      }
    },
    [submitAccessRequest]
  )

  const domainOptions = useMemo(() => {
    return DATA_PRODUCT_DOMAINS.map((d) => ({
      label: d.name,
      value: d.name
    }))
  }, [])

  // Fetch data products when modal opens
  useEffect(() => {
    if (isOpen) {
      const loadDataProducts = async () => {
        const products = await fetchAvailableDataProducts(0, 100)
        setDataProducts(products)
      }
      loadDataProducts()

      // Pre-select existing data products
      const existingIds = existingDataProducts
        .map((dp: any) => String(dp.dataProductExternalId || dp.id))
        .filter(Boolean)
      setSelectedRows(existingIds)
    }
  }, [isOpen])

  useImperativeHandle(ref, () => ({
    clearSelection: () => setSelectedRows([])
  }))

  // Filter by search + domain — same pattern as DataMarketplace page
  const filteredDataProducts = useMemo(() => {
    let filtered = dataProducts
    if (selectedDomain) {
      const selectedDomainValue = selectedDomain.value.toLowerCase().trim()
      filtered = filtered.filter((p) => {
        const productDomain = (p.dataCategory || '').toLowerCase().trim()
        return productDomain === selectedDomainValue
      })
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (p) => p.name.toLowerCase().includes(term) || p.description.toLowerCase().includes(term)
      )
    }
    return filtered
  }, [dataProducts, selectedDomain, searchTerm])

  const handleBrowseMarketplace = () => {
    toggle()
    navigate('/data-marketplace')
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="xl"
      className="full-centered-modal"
      contentClassName="theme-surface !rounded-lg overflow-hidden">
      {/* Header — using ModalHeader pattern from other modals for proper close button */}
      <ModalHeader
        className="!justify-between !border-0 !flex !items-center !bg-[var(--color-primary)] !py-2.5 !px-5 flex-shrink-0"
        toggle={toggle}
        close={
          <IconX
            size={24}
            onClick={toggle}
            className="text-white transition-opacity duration-300 ease-in-out hover:opacity-80 cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center gap-2.5">
          <IconDatabase size={22} className="text-white" />
          <span className="text-white font-semibold text-lg leading-[26px]">Data Marketplace</span>
        </div>
      </ModalHeader>

      {/* Body */}
      <div className="flex flex-col gap-1 px-2 py-1 flex-1 min-h-0">
        {/* Search Input */}
        <InputGroup
          addon={<IconSearch size={16} />}
          addonPosition="start"
          addonType="merged"
          placeholder="Search for data products..."
          value={searchTerm}
          className="w-full"
          onChange={(e: any) => setSearchTerm(e.target.value)}
        />

        {/* Domain Filter Pills — same pattern as DataMarketplace page */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div
            ref={pillsRef}
            className="flex items-center gap-1 overflow-x-auto flex-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden whitespace-nowrap">
            <Badge
              label="All"
              type="pill"
              size="medium"
              className={`cursor-pointer px-1 font-semibold ${
                !selectedDomain ? 'theme-badge-filter-active' : 'theme-badge-filter-inactive'
              }`}
              onClick={() => setSelectedDomain(null)}
            />
            {domainOptions.map((domain, index) => {
              const isActive = selectedDomain?.value === domain.value
              return (
                <Badge
                  key={`${domain.value || domain.label}-${index}`}
                  label={domain.label}
                  type="pill"
                  size="medium"
                  className={`cursor-pointer px-1 font-semibold ${
                    isActive ? 'theme-badge-filter-active' : 'theme-badge-filter-inactive'
                  }`}
                  onClick={() =>
                    setSelectedDomain((prev: any) => {
                      return prev?.value === domain.value
                        ? null
                        : { label: domain.label, value: domain.value }
                    })
                  }
                />
              )
            })}
          </div>
        </div>

        {/* Data Products List + Preview */}
        <div
          className="flex-1 min-h-0 flex overflow-hidden"
          onMouseLeave={() => setHoveredProduct(null)}>
          {/* Product List */}
          <div className="w-full theme-surface transition-all duration-300 overflow-hidden flex flex-col gap-1.5">
            {/* Product List */}
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
              {isLoadingDataProducts ? (
                <div className="flex items-center justify-center py-10">
                  <Spinner color="primary" />
                </div>
              ) : filteredDataProducts.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                  <span className="theme-text-subheading text-sm">No data products found</span>
                </div>
              ) : (
                <div className="flex flex-col">
                  {filteredDataProducts.map((product) => (
                    <div
                      key={product.id}
                      className={`flex items-center transition-colors theme-list-card group relative cursor-pointer p-[5px] ${
                        hoveredProduct?.id === product.id ? 'is-active' : ''
                      }`}
                      onMouseEnter={() => setHoveredProduct(product)}
                      onClick={() => {
                        toggle()
                        navigate(`/data-marketplace/${product.id}/details`)
                      }}>
                      {/* Tier Badge */}
                      <div className="rounded-lg flex items-center justify-center mr-3">
                        <div
                          className={`w-10 h-10 ${getTierColor(
                            product.tier
                          )} text-white text-sm font-bold rounded flex items-center justify-center flex-shrink-0`}>
                          {getTierAbbr(product.tier)}
                        </div>
                      </div>
                      {/* Content */}
                      <div className="grow min-w-0">
                        <p className="card-header-text !mb-0 pt-[4px] truncate">{product.name}</p>
                        <p className="caption-text mb-0 line-clamp-1 text-ellipsis">
                          {product.description || 'No description available'}
                        </p>
                      </div>
                      {/* Access Status */}
                      <div className="flex-shrink-0 ml-2 inline-flex items-center whitespace-nowrap min-w-[90px] pr-9">
                        <span className="inline-flex items-center gap-1.5 border border-[var(--border-default)] rounded px-2.5 py-[3px] text-s font-medium theme-text-heading bg-[var(--surface-bg)]">
                          <span
                            className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${
                              product.accessStatus === 'FULL_ACCESS'
                                ? 'bg-[var(--color-success)]'
                                : product.accessStatus === 'PENDING_ACCESS'
                                ? 'bg-[var(--color-warning)]'
                                : 'bg-[var(--color-danger)]'
                            }`}
                          />
                          {product.accessStatus === 'FULL_ACCESS'
                            ? 'Full access'
                            : product.accessStatus === 'PENDING_ACCESS'
                            ? 'Pending'
                            : 'No access'}
                        </span>
                      </div>
                      {/* Hover Chevron — absolutely positioned so it doesn't shift status left */}
                      <div className="absolute right-[5px] opacity-0 group-hover:!opacity-80 transition-opacity rounded p-0.5">
                        <IconChevronRight className="theme-text-subheading" size={20} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Preview Card (shown on hover) */}
          {hoveredProduct && (
            <div className="w-[380px] h-full flex-shrink-0 p-0.5 transition-all duration-300 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="theme-surface theme-card-border !rounded-[8px] flex flex-col">
                {/* Image with tier badge */}
                <div className="px-[24px] pt-[10px]">
                  <div className="relative overflow-hidden h-[100px] w-full rounded-[2px]">
                    <div className="absolute inset-0 [clip-path:polygon(0_0,60%_0,75%_100%,0_100%)]">
                      <img
                        src={appendToken(`${imgPath}/${hoveredProduct.name}.jpg`)}
                        alt=""
                        className="w-full h-full object-cover rounded-[5px]"
                        onError={(e) => {
                          e.currentTarget.onerror = null
                          e.currentTarget.src = appendToken(`${imgPath}/data-product.jpg`)
                        }}
                      />
                    </div>
                    <div className="absolute inset-0 pointer-events-none theme-surface z-10 [clip-path:polygon(60%_0,60.04%_0,75.04%_100%,75%_100%)]" />
                    <div className="absolute inset-0 [clip-path:polygon(61%_0,100%_0,100%_100%,76%_100%)]">
                      <div
                        className={`w-full h-full rounded-[5px] ${getTierColor(
                          hoveredProduct.tier
                        )}`}
                      />
                      <div className="absolute top-[16px] right-[10px] w-[100px] text-right">
                        <span className="text-[10px] leading-[14px] tracking-[0.2px] opacity-60 uppercase text-white">
                          TIER
                        </span>
                        <span className="block text-white text-sm font-extrabold">
                          {hoveredProduct.tier === 'Business Optimised'
                            ? 'BUSINESS\nOPTIMISED'
                            : hoveredProduct.tier === 'Curated'
                            ? 'CURATED'
                            : 'SOURCE'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title + Description */}
                <div className="px-[24px] pt-[16px]">
                  <h5 className="!mb-0 overflow-hidden text-ellipsis whitespace-nowrap text-[20px] leading-[28px] font-bold theme-text-heading">
                    {hoveredProduct.name}
                  </h5>
                  <p className="line-clamp-2 text-[14px] leading-[22px] font-normal theme-text-body mb-[8px]">
                    {hoveredProduct.description}
                  </p>
                  <hr className="border-t border-[var(--divider-color)] my-1 w-full" />
                </div>

                {/* DOMAIN + SOURCE */}
                <div className="flex gap-[16px] px-[24px] mb-0.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.2px] uppercase font-semibold theme-text-subheading">
                      DOMAIN
                    </div>
                    <span className="text-[16px] leading-[24px] font-semibold theme-text-heading">
                      {hoveredProduct.dataCategory}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.2px] uppercase font-semibold theme-text-subheading">
                      SOURCE
                    </div>
                    <span className="text-[16px] leading-[24px] font-semibold theme-text-heading">
                      {hoveredProduct.dataProviders || 'Aladdin / RadX'}
                    </span>
                  </div>
                </div>

                {/* OWNER(S) + ACCESS */}
                <div className="flex gap-[16px] px-[24px] mb-[8px]">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.2px] uppercase font-semibold theme-text-subheading">
                      OWNER(S)
                    </div>
                    <span className="text-[16px] leading-[24px] font-semibold theme-text-heading">
                      {hoveredProduct.owners?.[0]?.name || 'David Lynn'}
                    </span>
                    {hoveredProduct.owners?.[0]?.department && (
                      <span className="block text-xs theme-text-subheading">
                        {hoveredProduct.owners[0].department}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.2px] uppercase font-semibold theme-text-subheading">
                      ACCESS
                    </div>
                    <div className="h-[40px] flex items-center py-[6px]">
                      {hoveredProduct.accessStatus === 'PENDING_ACCESS' ? (
                        <Badge
                          className="app-status-badge"
                          color="warning"
                          variant="light"
                          label="Pending"
                          type="pill"
                        />
                      ) : (
                        <span
                          className={`rounded-[15px] h-6 inline-flex items-center justify-center text-[12px] leading-[18px] font-medium px-1 ${
                            hoveredProduct.accessStatus === 'FULL_ACCESS'
                              ? 'theme-badge-primary-soft'
                              : 'theme-badge-danger'
                          }`}>
                          {hoveredProduct.accessStatus === 'FULL_ACCESS'
                            ? 'Full access'
                            : 'No access'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action button */}
                <div className="px-[24px] pb-[10px]">
                  {hoveredProduct.accessStatus === 'FULL_ACCESS' ? (
                    hoveredProduct.exploreUrl ? (
                      <Button
                        label="Open in SmartData Hub"
                        color="primary"
                        icon={<IconExternalLink size={16} />}
                        className="w-full justify-center"
                        onClick={async () => {
                          const exploreUrl = hoveredProduct.exploreUrl!
                          toggle()
                          try {
                            const appData = (await getApplication('Smart-Data-Hub'))?.data
                            if (appData?.url && appData?.name) {
                              let redirectPath = exploreUrl.trim()
                              const absoluteMatch = redirectPath.match(
                                /^[a-zA-Z][a-zA-Z\d+.-]*:\/\/[^/]+(\/.*)?$/
                              )
                              if (absoluteMatch) {
                                redirectPath = absoluteMatch[1] || '/'
                              }
                              const normalizedPath = encodeURI(decodeURIComponent(redirectPath))
                              const baseUrl = appData.url.endsWith('/')
                                ? appData.url.slice(0, -1)
                                : appData.url
                              dispatch(
                                launchApp({
                                  ...appData,
                                  url: baseUrl + normalizedPath,
                                  redirectUrl: normalizedPath
                                })
                              )
                            } else {
                              navigate(
                                `/workspace?activeApp=Smart-Data-Hub&redirectUrl=${encodeURIComponent(
                                  exploreUrl
                                )}`
                              )
                            }
                          } catch {
                            navigate(
                              `/workspace?activeApp=Smart-Data-Hub&redirectUrl=${encodeURIComponent(
                                exploreUrl
                              )}`
                            )
                          }
                        }}
                      />
                    ) : (
                      <Button
                        label="View Details"
                        color="primary"
                        icon={<IconExternalLink size={16} />}
                        className="w-full justify-center"
                        onClick={() => {
                          toggle()
                          navigate(`/data-marketplace/${hoveredProduct.id}`)
                        }}
                      />
                    )
                  ) : hoveredProduct.accessStatus === 'PENDING_ACCESS' ? (
                    <Button
                      label="Pending Request"
                      variant="outline"
                      icon={<IconLock size={16} />}
                      className="w-full justify-center theme-warning-light-btn"
                      disabled
                    />
                  ) : (
                    <Button
                      label={
                        accessCache[hoveredProduct.id]?.isLoading ? 'Loading...' : 'Request Access'
                      }
                      color="primary"
                      icon={<IconLock size={16} />}
                      className="w-full justify-center"
                      disabled={accessCache[hoveredProduct.id]?.isLoading}
                      onClick={() => handleRequestAccess(hoveredProduct)}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-2 flex-shrink-0">
          <Button label="Cancel" color="neutral" variant="flat" onClick={toggle} />

          {/* Right buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              label="Browse Data Marketplace"
              variant="outline"
              color="neutral"
              className="theme-neutral-outline-btn"
              icon={<IconDatabase size={16} />}
              onClick={handleBrowseMarketplace}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

DataMarketplaceModal.displayName = 'DataMarketplaceModal'

export default DataMarketplaceModal
